import React, { FunctionComponent, useCallback, useEffect, useMemo, useState } from 'react';

import Translate from 'ui/atoms/translate';
import NarrowContent from 'core/layout/content/narrow-content';
import BoxedContent from 'ui/molecules/boxed-content';
import PublicPage from 'core/auth/components/public-page';
import { useApiCall } from 'hooks/use-api-call';
import { UsersApi } from 'api/apis';
import ActionButtons from 'ui/molecules/action-buttons';
import Button from 'ui/atoms/button';
import useGoTo from 'src/hooks/use-go-to';
import { AUTH_ROUTES } from '../routes.config';
import Hint from 'src/ui/atoms/hint';
import { AdminConfirmationSerialiserRequest, LoginTypeEnum, UserAfterConfirmation } from 'src/api';
import applicationConfig from 'core/config/local';

const OrganizationInvitationPage: FunctionComponent<{}> = () => {
  const { withApi, makeAuthenticatedApi } = useApiCall();

  const [passwordSetupEmailSent, setPasswordSetupEmailSent] = useState(false);
  const [confirmedUser, setConfirmedUser] = useState<UserAfterConfirmation>();

  const api: UsersApi = useMemo(() => {
    return makeAuthenticatedApi(UsersApi);
  }, [makeAuthenticatedApi]);

  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const token = searchParams.get('token');
    const user = searchParams.get('user');
    const params: AdminConfirmationSerialiserRequest = {
      user: user || '',
      token: token || '',
    };
    withApi(async () => {
      const userAfterConfirmation = await api.usersAdminUserConfirmationCreate({
        adminConfirmationSerialiserRequest: params,
      });
      setConfirmedUser(userAfterConfirmation);
    });
  }, []);

  const onSetPasswordClick = useCallback(() => {
    withApi(async () => {
      await api.usersPasswordResetCreate({
        resetPasswordRequest: {
          email: confirmedUser?.email || '',
          distributionPlatformName: applicationConfig.platform,
        },
      });
      setPasswordSetupEmailSent(true);
    });
  }, [withApi, api, confirmedUser]);

  const redirectToLogin = useGoTo(AUTH_ROUTES.login);

  return (
    <PublicPage>
      <NarrowContent>
        <BoxedContent
          title={
            passwordSetupEmailSent ? (
              <Translate name={'organizationInvitation.setPassword'} />
            ) : (
              confirmedUser?.organization.name
            )
          }
        >
          {passwordSetupEmailSent ? (
            <Hint variant="success">
              <Translate name={'organizationInvitation.passwordEmailSent'} args={[confirmedUser?.email]} />
            </Hint>
          ) : (
            <>
              <Hint variant="success">
                <Translate name={'organizationInvitation.hint'} args={[confirmedUser?.organization.name]} />
              </Hint>
              {confirmedUser?.organization.loginType === LoginTypeEnum.PASSWORD ? (
                <ActionButtons>
                  <Button variant="primary" size="large" onClick={onSetPasswordClick}>
                    <Translate name={'organizationInvitation.setPassword'} />
                  </Button>
                </ActionButtons>
              ) : (
                <ActionButtons>
                  <Button variant="primary" size="large" onClick={() => redirectToLogin()}>
                    <Translate name={'organizationInvitation.login'} />
                  </Button>
                </ActionButtons>
              )}
            </>
          )}
        </BoxedContent>
      </NarrowContent>
    </PublicPage>
  );
};

export default OrganizationInvitationPage;
